export interface CheckRemediation {
    whatHappened: string;
    whatToDo: string;
}

export const CHECK_REMEDIATION: Record<string, CheckRemediation> = {
    SUM_REV_TO_PNL: {
        whatHappened: "The revenue total in the model does not match the P&L report.",
        whatToDo: "Refresh data from Xero, then check if any revenue accounts are unmapped in Category Mapping.",
    },
    SUM_EXP_TO_PNL: {
        whatHappened: "The expense total in the model does not match the P&L report.",
        whatToDo: "Refresh data from Xero, then check if any expense accounts are unmapped in Category Mapping.",
    },
    CF_BANK_OPENING_TIE: {
        whatHappened: "Opening cash balance does not match the bank summary report.",
        whatToDo: "Verify opening balances in the Cash Flow tab. Re-sync from Xero if data looks stale.",
    },
    CF_BANK_SUMMARY_TIE: {
        whatHappened: "Closing cash balance does not match the bank summary report.",
        whatToDo: "Check for missing bank transactions. Re-sync and review any unreconciled items.",
    },
    RECON_REVENUE: {
        whatHappened: "Revenue classification totals do not reconcile with source data.",
        whatToDo: "Review revenue account mappings in Category Mapping. Look for accounts assigned to wrong categories.",
    },
    RECON_COGS: {
        whatHappened: "Cost of Sales classification does not reconcile with source data.",
        whatToDo: "Review direct cost account mappings. Ensure COGS accounts are correctly classified.",
    },
    RECON_EXPENSES: {
        whatHappened: "Operating Expense classification does not reconcile with source data.",
        whatToDo: "Review expense account mappings. Check for accounts that may be double-counted or misclassified.",
    },
    LEDGER_PNL_ACCOUNT_TIE: {
        whatHappened: "P&L account totals do not match the underlying journal entries.",
        whatToDo: "Re-sync journal data from Xero. Check for recent manual journals that may not be reflected.",
    },
    MONTH_PNL_TIE: {
        whatHappened: "Monthly P&L totals do not tie across periods.",
        whatToDo: "Check for transactions posted to incorrect periods. Re-sync and compare month-by-month.",
    },
    LEDGER_CF_DIRECT_RECON: {
        whatHappened: "Cash flow statement does not reconcile with ledger journal entries.",
        whatToDo: "Review bank account mappings and ensure all cash-related journals are captured.",
    },
    LEDGER_CF_BANK_TIE: {
        whatHappened: "Cash flow does not tie to the bank account ledger.",
        whatToDo: "Check for transfers between bank accounts. Ensure all bank accounts are included in the model.",
    },
    BS_BALANCE_CHECK: {
        whatHappened: "Balance sheet does not balance (Assets ≠ Liabilities + Equity).",
        whatToDo: "Look for unmapped accounts or missing account types. Re-sync data and check retained earnings.",
    },
    PNL_ACCOUNT_TIE: {
        whatHappened: "P&L account-level values do not match the report totals.",
        whatToDo: "Re-sync data. Check if any accounts have been added or removed since last sync.",
    },
    CF_BANK_TIE: {
        whatHappened: "Cash flow does not tie to the bank report.",
        whatToDo: "Compare bank report totals with cash flow. Check for timing differences or missing transactions.",
    },
    DATA_FRESHNESS: {
        whatHappened: "Xero data has not been synced recently (over 24 hours ago).",
        whatToDo: "Re-sync data from Xero to ensure the model reflects the latest transactions.",
    },
    MAPPING_COVERAGE: {
        whatHappened: "Some Xero accounts are not mapped to model categories.",
        whatToDo: "Open Category Mapping and assign unmapped accounts to the correct P&L or Balance Sheet categories.",
    },
    MISSING_PERIODS: {
        whatHappened: "There are gaps in the monthly journal data — some months have no transactions.",
        whatToDo: "Check if Xero has data for the missing periods. Re-sync or verify that no journals exist for those months.",
    },
    SRC_PAGINATION_COMPLETE: {
        whatHappened: "Not all journal pages were retrieved during the last Xero sync.",
        whatToDo: "Re-sync from Xero. If the issue persists, check for API rate limits or connectivity problems.",
    },
    SRC_PULL_INTEGRITY: {
        whatHappened: "The synced journal totals do not match the Xero report totals.",
        whatToDo: "Re-sync data from Xero. Compare journal counts and totals to identify missing or duplicate entries.",
    },
};

export function getRemediation(checkId: string): CheckRemediation | null {
    return CHECK_REMEDIATION[checkId] ?? null;
}
